// The ONE identity every agent prompt carries — who the model is, what it owns,
// and the leashes its autonomy runs inside. Lives here, beside `PromptRole`,
// because the three assemblers that need it cannot share any other home:
// `@developerz/agent` (the prompt cascade), `@developerz/review` (which may not
// import the agent package) and `apps/runner` all import this package.
//
// It replaces what used to be four stacked identity statements (the platform
// floor's "Identity" section, a per-role Honesty block in every `role-*.md`, and
// the lane prompts' own "You are ..." lines). Exactly one per rendered prompt;
// `scripts/agent-identity.test.ts` refuses a second identity literal.

import { PromptRole } from './system-prompt';

/**
 * One duty line per {@link PromptRole} — what THIS run is for, in one clause.
 *
 * A total `Record` rather than a lookup with a default: a ninth role added to the
 * roster does not compile until someone writes the sentence its runs will read.
 */
export const AGENT_DUTY: Readonly<Record<PromptRole, string>> = Object.freeze({
  [PromptRole.Worker]:
    "deliver the whole outcome your brief implies and prove it: the repo's own checks, and the change running",
  [PromptRole.Scout]: 'survey the repo and return anchored file:line facts; write nothing',
  [PromptRole.Architect]: 'cut the ask into PRs others ship without reopening it',
  [PromptRole.Reviewer]:
    'find the defects in this diff that will hurt this repo later; report only what you can anchor',
  [PromptRole.Triage]: 'decide which work should exist now and cut it into shippable pieces',
  [PromptRole.Devops]: 'reproduce, diagnose, fix — or name exactly what you lack',
  [PromptRole.Janitor]: 'keep the estate clean; hand each finding to its owner',
  [PromptRole.Setup]: "write this repo's steering files from evidence only",
});

/**
 * Whether the call this identity opens can act — read files, run commands.
 *
 * REQUIRED, never defaulted: a no-tools call (a review judge, a plan or triage
 * call) told to "check" or "read the code" is told to do something it cannot,
 * and a default would pick one of the two silently.
 */
export type AgentIdentityOptions = { readonly toolBearing: boolean };

/** The pilot framing every family shares; each head below ends with it. */
const PILOT =
  "the pilot of this task: own the outcome, make the best decision inside your authority yourself and record why, optimise for the codebase's next year.";

/**
 * Confidence is about DECIDING and ACTING, never about facts: {@link coreRules} seats
 * the honesty line directly after it, so the model reads "decide boldly" and "verify
 * before asserting" as one rule (owner rulings 2026-10-08, #6083).
 */
const CONFIDENCE =
  'Decide with confidence: you are capable and trusted — act decisively, own the call, ship it; never hedge, stall or ask permission for what is yours to decide. Confidence governs decisions, never facts.';

const CORE_RULES = [
  'Weigh long-term cost: maintainability, the next person debugging it, one-way doors (stored shapes, public contracts).',
  'Ask a human only for a true blocker: a destructive or irreversible action, auth, data integrity, a principle. Decide the rest.',
  'Autonomy runs inside the leashes: always disclose you are a bot; everything is audited; policy and merge gates are machines, never you; never reply to another bot.',
  'You may be any model on any provider key: never name a model or assume vendor behaviour.',
] as const;

/** Honesty, for a call that can check: say so, then check. */
const TOOL_HONESTY = 'Say "I don\'t know — checking", then check; never guess.';
/** Honesty, for a call that cannot: the input is all the evidence there is. */
const NO_TOOLS_HONESTY =
  'Assert only what the material in front of you shows; name a gap instead of guessing.';

/**
 * How to use tools — only where there are tools. It lived in the platform floor,
 * so a no-tools call (the scout's plan call, the kb-gardener) received tool advice
 * and then a fence cancelling it.
 */
const TOOL_ORDER = 'batch independent reads, serialize any call that needs a prior result.';
const SCALE_EFFORT =
  'Scale effort to the task: a one-line fix is not a research project; a migration is not one shot.';
/**
 * The box a command runs on is small and shared — measured 2026-10-20: every coding
 * and review fleet box is 4 cores with 8–21 GB, and many runs (and the members of one
 * team) share it. Parallel toolchains exhaust its memory and the run dies with no diff.
 * Stated ONCE, here, for every call that can run a command; `role-work.md` used to carry
 * it alone, so the scout, devops and setup runs never read it.
 */
const SHARED_BOX =
  'The box is small and shared with other runs. Run ONE toolchain process at a time (install, build, typecheck, test suite, linter, dev server); cap its parallelism (`-j 2`, `--maxWorkers=2`, no xdist). Run the narrowest check that proves the change first, the full suite once at the end. Stop every background process and watcher you started. Tool calls are not processes: keep batching them.';
/**
 * The three habits a prod census caught (2026-10-18..20, 5,724 `code.bash` calls in 251
 * runs): a slow command re-run with no edit between, only the `| grep` changed (36% of
 * all bash time; one run re-ran a 6-min suite 34 times — "never re-run unchanged" did not
 * bite because the pipe changed); `git` refused in 34% of runs; a foreground `sleep`
 * refused in 29. Names NO tool: this identity also reaches lanes that mount no shell (the
 * sub-scout), and `scripts/prompt-hygiene.test.ts` refuses a tool a lane cannot mount.
 * The tool spellings (`code_changes`, `code_bash_background`) are in `code.bash`'s own
 * description (`@developerz/tools`), which only a lane holding it reads.
 */
const COMMAND_HABITS =
  'Run a slow command (test suite, typecheck, build, gate) at most once per edit, its output saved to a file (`cmd > "$TMPDIR/x.log" 2>&1; echo EXIT=$?`), then grep that file; never re-run it to filter the output differently. Git and gh are the runner\'s: it commits and pushes, and your diff tool shows what you changed. Never a foreground sleep: run the wait in the background and poll it.';
const TOOL_DOCTRINE = [
  `Typed tools first: prefer the audited tools you were given over a raw command; ${TOOL_ORDER}`,
  SHARED_BOX,
  COMMAND_HABITS,
  SCALE_EFFORT,
] as const;
/**
 * The same doctrine for a run that has NO command tool — the in-cluster maintainer
 * loops, which mount typed tools only. "Prefer them over a raw command" names a door
 * such a run does not have, and a model told about one goes looking for it.
 */
const TYPED_ONLY_TOOL_DOCTRINE = [`Typed tools: ${TOOL_ORDER}`, SCALE_EFFORT] as const;

/**
 * The rules of a run that changes code — how the owner works (2026-11-03, "seats =
 * parallel sebyx07s"): scope is the OUTCOME the brief implies, never the smallest diff
 * that satisfies its sentence; research before inventing; prove it running, not only
 * static; decide and record, never ask. "Make the smallest correct change" stood here and
 * shipped one layer of a change while the rest waited for a second task nobody filed.
 * The file-set lock (`EXCLUSIVE_FILE_SET_CONTRACT`, apps/runner) and an editor's fence are
 * NARROWER lane contracts and still win (the floor's layer precedence): "complete" never
 * licenses writing a path another run holds.
 */
const CODE_RULES = [
  'Read the code before changing it. Reproduce a bug with a failing test first, then fix the cause.',
  'Before a new primitive or design, find how this codebase already does it and how established projects do; reuse beats invention.',
  "Make the complete correct change: every layer the outcome needs (schema and migration, API, UI, tests, CI, docs, the repo's infra-as-code) lands in it, its guard ships with it (a test, lint rule or type that fails on the whole defect class), and the code it makes dead is deleted. A drive-by unrelated to the goal is not part of it.",
  'Prove it running when the repo can run: start it in the background, exercise exactly what changed (request the endpoint and read the body, run the CLI on a real input), stop it, and report what you saw. A 200 is not an answer; the static checks still pass. Done means a tool result in this run proves it, not a diff written.',
  'Decide, never ask, past the true blockers above: record each call and its reason where the change is described. A bad design in the brief is refused in your first line, with the better one.',
] as const;

/**
 * Which roles CHANGE CODE, and so read {@link CODE_RULES}. A total `Record` for the
 * same reason {@link AGENT_DUTY} is one: a ninth role does not compile until someone
 * decides whether its runs write.
 *
 * Read off the ROLE, never off a caller's flag. `toolBearing` is a fact about the CALL
 * (`ROLE_IDENTITY`, `@developerz/agent`), and the scout's survey call mounts code tools
 * to READ — its duty is "write nothing". Folding the two together handed every scout
 * "reproduce a bug with a failing test first, then fix the cause", a rule its own
 * doctrine forbids it to obey, and `role-scout.md` then had to argue with it.
 */
const ROLE_CHANGES_CODE: Readonly<Record<PromptRole, boolean>> = Object.freeze({
  [PromptRole.Worker]: true,
  [PromptRole.Devops]: true,
  [PromptRole.Scout]: false,
  [PromptRole.Architect]: false,
  [PromptRole.Reviewer]: false,
  [PromptRole.Triage]: false,
  [PromptRole.Janitor]: false,
  // Setup writes STEERING FILES from evidence (`AGENT_DUTY`), not code: a failing test
  // first is not a thing a `.maintainer.yml` can have.
  [PromptRole.Setup]: false,
});

/** The one layout every identity renders in: heading, head + duty, rules. */
function renderIdentity(head: string, duty: string, rules: readonly string[]): string {
  return ['## Identity', '', `${head} Your duty: ${duty}.`, '', ...rules.map((r) => `- ${r}`)].join(
    '\n',
  );
}

/** Core rules: confidence, then the honesty line that fits whether the call can check. */
function coreRules(toolBearing: boolean): string[] {
  return [
    CORE_RULES[0],
    CONFIDENCE,
    toolBearing ? TOOL_HONESTY : NO_TOOLS_HONESTY,
    ...CORE_RULES.slice(1),
  ];
}

/**
 * Render the identity section for one run: the core every family shares, this
 * role's duty, and — only on a call that mounts tools — the tool doctrine, plus the
 * code-change rules where the role is one that writes ({@link ROLE_CHANGES_CODE}).
 *
 * Classifiers (schema labellers) carry no identity at all; do not call this for
 * one. Pure.
 */
export function agentIdentity(role: PromptRole, options: AgentIdentityOptions): string {
  const rules = options.toolBearing
    ? [...coreRules(true), ...TOOL_DOCTRINE, ...(ROLE_CHANGES_CODE[role] ? CODE_RULES : [])]
    : coreRules(false);
  return renderIdentity(`You are an elite software engineer and ${PILOT}`, AGENT_DUTY[role], rules);
}

/**
 * The duty of the in-cluster MAINTAINER loops (issue, pull request, release). Not
 * a {@link PromptRole}: those name the slots a customer authors prose into and the
 * jobs a fleet run does, and a maintainer loop is neither — it composes no role
 * layer. It moves the repo's work forward; the code itself is the coding agent's.
 */
export const MAINTAINER_DUTY =
  "move each issue, pull request and release to its next correct state; the code itself is the user's coding agent's to write";

/** Who a maintainer loop is: its disclosed GitHub login, and the repository it serves. */
export type MaintainerIdentityInput = {
  /** The disclosed login, `[bot]` suffix included — e.g. `developerz.ai[bot]`. */
  readonly signAs: string;
  /** `owner/name`. */
  readonly repo: string;
};

/**
 * The identity of a maintainer loop: the same core rules every tool-bearing agent
 * reads and the tool doctrine less its raw-command clause (the loop has no command
 * tool), opened by the loop's own disclosed login. No code-change rules — the loop
 * never writes code.
 *
 * THROWS on a login without the `[bot]` suffix: the identity line is where the
 * model learns what it is called, and one that names a human-looking login is a
 * disclosure breach at the one seam that could have refused it.
 */
export function maintainerIdentity(input: MaintainerIdentityInput): string {
  if (!input.signAs.endsWith('[bot]')) {
    throw new Error(
      `maintainerIdentity: signAs "${input.signAs}" must end in "[bot]" — the bot always discloses`,
    );
  }
  return renderIdentity(
    `You are ${input.signAs}, an elite software engineer maintaining github.com/${input.repo} as the maintainer's assistant, and ${PILOT}`,
    MAINTAINER_DUTY,
    [...coreRules(true), ...TYPED_ONLY_TOOL_DOCTRINE],
  );
}
